import { FileText, HandCoins, Percent } from "lucide-react";
import useTranslations from "@/shared/lang/useTranslations";
import type { QuoterQuoteData } from "../types/quoter.types";

type QuoterQuotationResultProps = {
  quote: QuoterQuoteData;
  hideCommission?: boolean;
  hideConverted?: boolean;
  showEffectiveRate?: boolean;
};

function formatAmount(value: number): string {
  return value.toLocaleString("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatRate(value: number): string {
  return value.toLocaleString("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
}

export const QuoterQuotationResult = ({
  quote,
  hideCommission = false,
  hideConverted = false,
  showEffectiveRate = false,
}: QuoterQuotationResultProps) => {
  const { t } = useTranslations("quoter");
  const { breakdown, fees, currency } = quote;
  const from = currency.from;
  const to = currency.to;
  const montoConvertido = breakdown.amount_received + fees.total_fee;

  const tasaEfectivaDirecta =
    breakdown.amount_sent > 0 ? breakdown.amount_received / breakdown.amount_sent : 0;
  const tasaEfectivaInversa =
    breakdown.amount_received > 0 ? breakdown.amount_sent / breakdown.amount_received : 0;

  return (
    <div className="rounded-2xl border border-green-500/20 bg-green-500/5 dark:bg-green-500/10 overflow-hidden mt-4">
      <div className="border-l-4 border-green-500 bg-white dark:bg-zinc-900/50 p-6">
        <h3 className="text-lg font-bold text-green-500 flex items-center gap-2 mb-4">
          <FileText className="size-5" />
          {t("result_title")}
        </h3>

        {showEffectiveRate && (
          <div className="mb-4 p-4 bg-gray-50 dark:bg-zinc-800/50 rounded-xl border border-gray-200 dark:border-zinc-700">
            <div className="flex items-center gap-2 text-green-500 font-medium mb-3">
              <span className="text-sm">{t("result_effective_rate_title")}</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 dark:text-zinc-400 uppercase font-bold tracking-tight">
                  1 {from} {t("step1_equiv_to")}
                </span>
                <span className="text-lg font-mono font-bold">
                  {formatRate(tasaEfectivaDirecta)} {to}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 dark:text-zinc-400 uppercase font-bold tracking-tight">
                  1 {to} {t("step1_equiv_to")}
                </span>
                <span className="text-lg font-mono font-bold">
                  {formatRate(tasaEfectivaInversa)} {from}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-0 divide-y divide-dotted divide-gray-200 dark:divide-zinc-700">
          <div className="flex items-center gap-3 py-3">
            <HandCoins className="size-5 text-gray-400 dark:text-zinc-500 shrink-0" />
            <span className="text-sm text-gray-600 dark:text-zinc-400 flex-1">
              {t("result_amount_sent")}
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {formatAmount(breakdown.amount_sent)} {from}
            </span>
          </div>

          <div className="flex items-center gap-3 py-3">
            <HandCoins className="size-5 text-gray-400 dark:text-zinc-500 shrink-0" />
            <span className="text-sm text-gray-600 dark:text-zinc-400 flex-1">
              Monto que recibirá
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              {formatAmount(breakdown.amount_received)} {to}
            </span>
          </div>

          {!hideConverted && (
            <div className="flex items-center gap-3 py-3">
              <FileText className="size-5 text-gray-400 dark:text-zinc-500 shrink-0" />
              <span className="text-sm text-gray-600 dark:text-zinc-400 flex-1">
                {t("result_amount_converted")}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {formatAmount(montoConvertido)} {to}
              </span>
            </div>
          )}

          {!hideCommission && (
            <div className="flex items-center gap-3 py-3">
              <Percent className="size-5 text-gray-400 dark:text-zinc-500 shrink-0" />
              <span className="text-sm text-gray-600 dark:text-zinc-400 flex-1">
                {t("result_commission", { percent: fees.percentage_fee })}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                -{formatAmount(fees.total_fee)} {to}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
